<template>
    <div
        id="lt-yantr"
        class="CreditCardInterest--root advanced-calc">
        <div class="CreditCardInterest--container">
            <div class="CreditCardInterest--outer">
                <div class="CreditCardInterest--info">
                    <h2 class="CreditCardInterest--title">
                        Credit Card Interest Calculator
                    </h2>
                    <p class="CreditCardInterest--description">
                        Determine what it will take to pay off your credit card balance.
                    </p>
                </div>
                <div class="CreditCardInterest--wrapper">
                    <div class="CreditCardInterest--form-group">
                        <div class="CreditCardInterest--filter-inputs">
                            <form
                                ref="form"
                                @submit.prevent="creditCardInterestCalculation()">
                                <ul class="CreditCardInterest--filter-block">
                                    <li>
                                        <label>{{ labelBalanceAmount }}</label>
                                        <number-input
                                            v-model="balanceAmountNumber"
                                            input-name="balanceAmount"
                                            inputmode="decimal"
                                            maxlength="6"
                                            minvalue="1"
                                            :show-symbol="false"
                                            no-decimal="true"
                                            @inputBlur="creditCardInterestCalculation()">
                                            <template #leftimage>
                                                <div class="input-group-addon">
                                                    <img src="../../assets/images/dollar-icon.svg" />
                                                </div>
                                            </template>
                                        </number-input>
                                    </li>
                                    <li>
                                        <label>{{ labelInterestRate }}</label>
                                        <number-input
                                            v-model="InterestRate"
                                            precision="2"
                                            max-value="99.99"
                                            input-name="averageInterestRate"
                                            :show-symbol="false"
                                            maxlength="4"
                                            minvalue="0"
                                            inputmode="decimal"
                                            @inputBlur="creditCardInterestCalculation()">
                                            <template #rightimage>
                                                <div class="input-group-addon">
                                                    <img src="../../assets/images/percentage-icon.svg" />
                                                </div>
                                            </template>
                                        </number-input>
                                    </li>
                                    <li>
                                        <label><strong>How would you like to calculate?</strong></label>
                                        <br />
                                        <div class="CreditCardInterest--radio-section">
                                            <div class="CreditCardInterest--button">
                                                <input
                                                    id="monthly"
                                                    type="radio"
                                                    name="yearPayment"
                                                    :checked="showHideMonthInputForCalculation === 1 ? 'checked' : ''"
                                                    @change="showHideInput(1)"
                                                />
                                                <label
                                                    for="monthly"
                                                    class="CreditCardInterest--button-label"> By Monthly Payment</label>
                                            </div>
                                            <div class="CreditCardInterest--button">
                                                <input
                                                    id="payoff"
                                                    type="radio"
                                                    name="yearPayment"
                                                    :checked="showHideMonthInputForCalculation === 0 ? 'checked' : ''"
                                                    @change="showHideInput(0)"
                                                />
                                                <label
                                                    for="payoff"
                                                    class="CreditCardInterest--button-label"> By Months to Pay Off</label>
                                            </div>
                                        </div>
                                    </li>
                                    <li>
                                        <div v-if="showHideMonthInputForCalculation === 1">
                                            <label for="byMonthlyPayment">Monthly Payment</label>
                                            <number-input
                                                v-model="MorePerMonth"
                                                precision="2"
                                                input-name="byMonthlyPayment"
                                                inputmode="decimal"
                                                maxlength="6"
                                                minvalue="1"
                                                no-decimal="false"
                                                :show-symbol="false"
                                                :class="error.monthlyPaymentError ? 'error-border-red' : ''"
                                                @inputBlur="creditCardInterestCalculation()">
                                                <template #leftimage>
                                                    <div class="input-group-addon">
                                                        <img src="../../assets/images/dollar-icon.svg" />
                                                    </div>
                                                </template>
                                                <template #rightimage>
                                                    <div
                                                        v-if="error.monthlyPaymentError"
                                                        class="input-group-addon error-image">
                                                        <img src="../../assets/images/error-icon.svg" />
                                                    </div>
                                                </template>
                                                <template #errormessage>
                                                    <div
                                                        class="error-text"
                                                        :class="error.monthlyPaymentError ? 'error-red-text' : ''">
                                                        <p>Please enter a monthly payment amount greater than ${{ minMonthlyPayment }} to see repayment breakdown.</p>
                                                    </div>
                                                </template>
                                            </number-input>
                                        </div>
                                        <div v-else>
                                            <label>Months to Pay Off</label>
                                            <number-input
                                                v-model="YearPayOff"
                                                precision="0"
                                                input-name="byMonthsPayOff"
                                                inputmode="decimal"
                                                maxlength="6"
                                                minvalue="1"
                                                no-decimal="true"
                                                symbol="months"
                                                symbol-aligned="right"
                                                @inputBlur="creditCardInterestCalculation()"
                                            />
                                        </div>
                                    </li>
                                    <li class="CreditCardInterest--btn-wrapper">
                                        <button
                                            class="btn CreditCardInterest--calculate-btn"
                                            type="submit">
                                            {{ calculationButtonText }}
                                        </button>
                                        <button
                                            class="btn CreditCardInterest--reset-btn"
                                            type="button"
                                            @click="resetFilter()">
                                            {{ resetButtonText }}
                                        </button>
                                    </li>
                                </ul>
                            </form>
                        </div>
                    </div>
                    <div class="CreditCardInterest--result-wrapper">
                        <div class="CreditCardInterest--result-container">
                            <div class="CreditCardInterest--result-top">
                                <div class="CreditCardInterest--result-description">
                                    <div class="CreditCardInterest--result-payoff">
                                        <div class="CreditCardInterest--result-headline">
                                            Months to Pay Off:
                                        </div>
                                        <div
                                            v-if="MonthsPayoff"
                                            class="CreditCardInterest--result-amount hidden-xs hidden-sm">
                                            {{ MonthsPayoff }} months
                                        </div>
                                        <div
                                            v-if="MonthsPayoff"
                                            class="CreditCardInterest--result-amount hidden-md hidden-lg">
                                            {{ MonthsPayoff }} mo.
                                        </div>
                                        <div
                                            v-else
                                            class="CreditCardInterest--result-amount">
                                            -
                                        </div>
                                    </div>
                                    <div class="CreditCardInterest--result-payment">
                                        <div class="CreditCardInterest--result-headline">
                                            Monthly Payment:
                                        </div>
                                        <div
                                            v-if="MonthlyPayment"
                                            class="CreditCardInterest--result-amount">
                                            ${{ MonthlyPayment }}
                                        </div>
                                        <div
                                            v-else
                                            class="CreditCardInterest--result-amount">
                                            -
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="CreditCardInterest--result-bottom">
                                <div class="chart-parent">
                                    <vue-chart
                                        id="donutChart"
                                        type="doughnut"
                                        :data="chartData"
                                        :options="options"
                                        :width="chartWidth"
                                        :height="chartHeight"
                                    />
                                    <div class="donut-inner">
                                        <label>Total Repayment:</label>
                                        <h3>{{ TotalPaymentView }}</h3>
                                    </div>
                                </div>
                                <div class="CreditCardInterest--result-breakdown">
                                    <h4 class="CreditCardInterest--result-title">
                                        Repayment breakdown
                                    </h4>
                                    <ul class="CreditCardInterest--result-list">
                                        <li>
                                            <label><span class="CreditCardInterest--result-teal-bg"></span> Total Interest Paid</label>
                                            <span
                                                v-if="InterestPaid"
                                                class="CreditCardInterest--result-value">${{ formatNumber(InterestPaid) }}</span>
                                            <span
                                                v-else
                                                class="CreditCardInterest--result-value">-</span>
                                        </li>
                                        <li>
                                            <label><span class="CreditCardInterest--result-green-bg"></span> Principal Amount</label>
                                            <span class="CreditCardInterest--result-value">${{ formatNumber(BalanceAmountView) }}</span>
                                        </li>
                                    </ul>
                                    <div class="CreditCardInterest--result-disclosure">
                                        {{ disclosureText }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import Accounting from 'accounting';
    import VueChart from 'vue-chart-js';
    import NumberInput from '~/components/number-input.vue';
    import ChartTooltip from '~/helpers/chart-tooltip';

    export default {
        components: {
            NumberInput,
            VueChart,
        },
        props: {
            labelInterestRate: {
                default: 'Interest Rate (APR)',
            },
            labelBalanceAmount: {
                default: 'Balance',
            },
            interestRate: {
                default: 20,
            },
            balanceAmount: {
                default: 5000,
            },
            byMonthsPayOff: {
                default: 36,
            },
            byMonthlyPayment: {
                default: 250,
            },
            calculationButtonText: {
                default: 'Calculate',
            },
            resetButtonText: {
                default: 'Reset',
            },
            yearPayment: {
                default: 0,
            },
            disclosureText: {
                default: 'This calculator is a self help tool and is not intended to provide financial advice to its users. We cannot guarantee the accuracy of results when applied to a user’s individual factors.',
            },
        },
        data() {
            return {
                InterestRate: Accounting.formatMoney(this.interestRate, { symbol: '', precision: 2 }),
                BalanceAmount: Accounting.format(this.balanceAmount),
                BalanceAmountView: Accounting.format(this.balanceAmount),
                TotalPaymentView: this.getTotalPayment(),
                CalculationButtonText: this.calculationButtonText,
                ResetButtonText: this.resetButtonText,
                YearPayOff: this.byMonthsPayOff,
                MorePerMonth: Accounting.format(this.byMonthlyPayment),
                showHideMonthInputForCalculation: this.yearPayment,
                minMonthlyPayment: '',
                error: {
                    monthlyPaymentError: false,
                },
                chartData: {
                    labels: [],
                    datasets: [
                        {
                            data: [],
                            backgroundColor: [
                                '#078181',
                                '#08c177',
                            ],
                        },
                    ],
                },
                options: {
                    cutoutPercentage: 80,
                    percentageInnerCutout: 80,
                    segmentShowStroke: true,
                    segmentStrokeColor: '#fff',
                    segmentStrokeWidth: 5,
                    legend: {
                        display: false,
                    },
                    tooltips: {
                        enabled: false,
                        custom: ChartTooltip.customTooltips,
                    },
                },
                chartWidth: 204,
                chartHeight: 204,
                MonthlyPayment: null,
                InterestPaid: null,
                MonthsPayoff: null,
            };
        },
        computed: {
            balanceAmountNumber: {
                set(newValue) {
                    this.BalanceAmount = Accounting.format(newValue);
                },
                get() {
                    return Number(Accounting.unformat(this.BalanceAmount));
                },
            },
        },
        mounted() {
            this.showHideInput(this.showHideMonthInputForCalculation);
            this.creditCardInterestCalculation();
        },
        methods: {
            showHideInput(value) {
                this.showHideMonthInputForCalculation = value;
            },
            formatNumber(figure) {
                return Accounting.format(figure);
            },
            calculatePercentage(value, divider) {
                return Math.round(value / divider * 100);
            },
            creditCardInterestCalculation() {
                const balance = Accounting.unformat(this.$refs.form.balanceAmount.value);
                const averageInterestRate = this.$refs.form.averageInterestRate.value;
                const minMonthlyPayment = balance * averageInterestRate / 12 / 100;
                this.minMonthlyPayment = Accounting.formatMoney(minMonthlyPayment, { symbol: '', precision: 2 });

                const numberOfMonthsOrMonthPayment = Accounting.unformat(this.showHideMonthInputForCalculation === 1 ? this.$refs.form.byMonthlyPayment.value : this.$refs.form.byMonthsPayOff.value);

                if (this.showHideMonthInputForCalculation === 1) {
                    if (numberOfMonthsOrMonthPayment >= Accounting.unformat(this.minMonthlyPayment)) {
                        this.error.monthlyPaymentError = false;
                        const byMonthlyPayment = this.calculateCreditCardPaymentByMonthlyPayment(balance, averageInterestRate, numberOfMonthsOrMonthPayment);
                        this.MonthlyPayment = Math.round(byMonthlyPayment.numberOfMonthsOrMonthPayment);
                        this.InterestPaid = Math.round(byMonthlyPayment.totalInterestPaid);
                        this.MonthsPayoff = Math.round(byMonthlyPayment.monthsPayoff);
                    } else {
                        this.error.monthlyPaymentError = true;
                    }
                } else {
                    const byMonthsPayOff = this.calculateCreditCardPaymentByMonthsToPayOff(balance, averageInterestRate, numberOfMonthsOrMonthPayment);
                    this.MonthlyPayment = Math.round(byMonthsPayOff.monthlyPayment);
                    this.InterestPaid = Math.round(byMonthsPayOff.totalInterestPaid);
                    this.MonthsPayoff = Math.round(byMonthsPayOff.numberOfMonthsOrMonthPayment);
                }
                this.initializeChartData(balance);

                this.BalanceAmountView = this.BalanceAmount;
                this.TotalPaymentView = this.getTotalPayment();
            },
            calculateCreditCardPaymentByMonthlyPayment(balance, averageInterestRate, numberOfMonthsOrMonthPayment) {
                const calculateBalance = numberOfMonthsOrMonthPayment - balance * averageInterestRate / 12 / 100;
                let result;
                if (calculateBalance !== 0) {
                    result = Math.log10(numberOfMonthsOrMonthPayment / calculateBalance);
                } else {
                    result = Math.log10(numberOfMonthsOrMonthPayment);
                }
                const calculateAverageInterest = Math.log10(1 + averageInterestRate / 12 / 100);

                const monthsPayoff = result / calculateAverageInterest;
                const totalInterestPaid = numberOfMonthsOrMonthPayment * monthsPayoff - balance;
                return {
                    totalInterestPaid,
                    monthsPayoff,
                    numberOfMonthsOrMonthPayment,
                };
            },
            calculateCreditCardPaymentByMonthsToPayOff(balance, averageInterestRate, numberOfMonthsOrMonthPayment) {
                const calculateBalance = balance * (averageInterestRate / 12 / 100) * (1 + averageInterestRate / 12 / 100) ** numberOfMonthsOrMonthPayment;
                const calculateAverageInterest = 1 + averageInterestRate / 12 / 100;
                const result = calculateAverageInterest ** numberOfMonthsOrMonthPayment - 1;
                const monthlyPayment = calculateBalance / result;
                const totalInterestPaid = monthlyPayment * numberOfMonthsOrMonthPayment - balance;
                return {
                    totalInterestPaid,
                    numberOfMonthsOrMonthPayment,
                    monthlyPayment,
                };
            },
            initializeChartData(balance) {
                this.chartData.datasets[0].data = [];
                this.chartData.labels = [];
                const totalPayment = this.InterestPaid + Accounting.unformat(balance);
                this.chartData.datasets[0].data.push(this.calculatePercentage(this.InterestPaid, totalPayment));
                this.chartData.datasets[0].data.push(this.calculatePercentage(balance, totalPayment));
                this.chartData.labels.push(`Total Interest Paid ${Accounting.formatMoney(this.InterestPaid, { precision: 0 })}`);
                this.chartData.labels.push(`Principal Amount ${Accounting.formatMoney(balance, { precision: 0 })}`);
            },
            resetFilter() {
                this.BalanceAmount = Accounting.format(this.balanceAmount);
                this.InterestRate = this.interestRate;
                this.YearPayOff = this.byMonthsPayOff;
                this.MorePerMonth = Accounting.format(this.byMonthlyPayment);
                this.showHideMonthInputForCalculation = this.yearPayment;
                this.$nextTick(() => {
                    this.creditCardInterestCalculation();
                });
            },
            getTotalPayment() {
                if (this.InterestPaid) {
                    return Accounting.formatMoney(this.InterestPaid + Accounting.unformat(this.BalanceAmount), { precision: 0 });
                }
                return Accounting.formatMoney(Accounting.unformat(this.BalanceAmount), { precision: 0 });
            },
        },
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="../../assets/styles/calc-advanced-filter.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
